function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect } from 'react';
import { string, array, arrayOf, oneOf, number, bool, shape, func, oneOfType } from 'prop-types';
import cn from 'clsx';
import warning from 'warning';
import EditorContent from '../EditorContent/index.jsx';
import Link from '../Link/index.jsx';
import LoadingIcon from '../../molecules/LoadingIcon/index.jsx';
import { useAmp } from '../../hooks/index.js';
import { imageSizes } from './sizes.js';
import Placeholder from '../Placeholder/index.jsx';
import { parseHtml } from '../../utils/index.js';

/**
 * Get the height scaled by the aspect ratio to use in image URLs.
 */
const getScaledHeight = (ratio, width, height) => {
  if (!ratio) {
    return height;
  }
  return ratio * width;
};

/**
 * Get the height scaled proportionally to the original width.
 */
const getProportionalHeight = (newWidth, originalWidth, originalHeight) => {
  if (!Number.isFinite(originalWidth) || !Number.isFinite(originalHeight)) {
    return null;
  }
  return Math.round(newWidth * (originalWidth / originalHeight));
};

/**
 * Get the resize parameters in the form "width,height".
 *
 * Or just "width" if that is all that is available (or nothing if no width
 * available).
 */
const getResizeParam = (ratio, width, height) => {
  const scaledHeight = getScaledHeight(ratio, width, height);
  if (!Number.isFinite(width)) {
    return null;
  }
  if (!Number.isFinite(scaledHeight)) {
    return Math.round(width);
  }
  return [Math.round(width), Math.round(scaledHeight)].join();
};

/**
 * Get the URI for an image with all parameters for the image service.
 *
 * See https://github.com/humanmade/tachyon/blob/master/docs/using.md
 */
const getImageUrl = ({
  url,
  width,
  height,
  quality,
  webp,
  crop,
  lbWidth,
  lbHeight,
  external,
  resizeStrategy,
  ratio
} = {}) => {
  if (external) {
    // Don't append params if the image is not passed through our image service
    return url;
  }
  const params = {
    quality,
    webp,
    crop: crop ? crop.map(n => `${Math.round(n)}px`).join(',') : null,
    lb: lbWidth ? `${Math.round(lbWidth)},${Math.round(lbHeight) || ''}` : null
  };

  // Add resize param if not using letterbox mode.
  if (!params.lb) {
    params[resizeStrategy] = getResizeParam(ratio, width, height);
  }
  const queryString = Object.keys(params).filter(key => params[key]).map(key => `${key}=${params[key]}`).join('&');
  return queryString.length ? `${url}?${queryString}` : url;
};

/**
 * Get the source set widths.
 *
 * If any srcset sizes that would attempt to request an image wider or taller
 * than the image's original source width or height then these are filtered out.
 *
 * If such filtering occurs, or there were no srcset widths less than or equal
 * to the original image size, we should return the largest size of image that
 * is available.
 */
const getSrcSetWidths = (srcSetBreakpoints, width, height, resizeStrategy) => {
  if (!Number.isFinite(width)) {
    return [];
  }
  const filteredBreakpoints = srcSetBreakpoints.filter(bp => {
    if (!height || width >= height || resizeStrategy === 'resize') {
      return bp <= width;
    }
    return getProportionalHeight(bp, width, height) <= height;
  });
  if (filteredBreakpoints.length < 1 || filteredBreakpoints[filteredBreakpoints.length - 1] < width && srcSetBreakpoints.length > filteredBreakpoints.length) {
    filteredBreakpoints.push(width);
  }
  return filteredBreakpoints;
};

/**
 * Generate a srcset.
 */
const generateSrcSet = ({
  url,
  width,
  height,
  quality,
  webp,
  crop,
  lbWidth,
  lbHeight,
  srcSetBreakpoints,
  resizeStrategy,
  ratio
}) => {
  const srcsetWidths = lbWidth ? [lbWidth] : getSrcSetWidths(srcSetBreakpoints, width, height, resizeStrategy);

  // Map each final srcset width to an image URL
  return srcsetWidths.map(srcsetWidth => `${getImageUrl({
    url,
    width: srcsetWidth,
    height: getProportionalHeight(srcsetWidth, width, height),
    quality,
    webp,
    crop,
    lbWidth,
    lbHeight,
    resizeStrategy,
    ratio
  })} ${srcsetWidth}w`);
};

/**
 * Get the float-based aspect ratio.
 *
 * This is primarily used to reserve whitespace and prevent content reflow.
 */
const getAspectRatio = (aspectRatio, width, height) => {
  // If the `aspectRatio` was given as a number use that directly.
  if (Number.isFinite(aspectRatio)) {
    return aspectRatio;
  }

  // If the `aspectRatio` is `dynamic` calculate from the width and height.
  // Note that this could also be achieved by passing through a numeric
  // `aspectRatio` but there are cases where this key variant is useful for
  // setting classes and the like.
  if (aspectRatio === 'dynamic') {
    return height / width;
  }

  // If the `aspectRatio` was given as a string map to one of our predefined
  // image aspect ratio numbers.
  if (aspectRatio in imageSizes) {
    return imageSizes[aspectRatio];
  }

  // If no `aspectRatio` was not given calculate from the width and height.
  if (width && height) {
    return height / width;
  }
  process.env.NODE_ENV !== "production" ? warning(aspectRatio !== 'dynamic' || Number.isFinite(width) && Number.isFinite(height), 'When using a dynamic aspect ratio a numerical width and height must be given, ' + `got width "${width}" and height "${height}".`) : void 0;
  process.env.NODE_ENV !== "production" ? warning(!aspectRatio, `If given, the aspect ratio must be a number, or one of ${Object.keys(imageSizes)}`) : void 0;
  return null;
};

/**
 * Get the resize strategy.
 *
 * We have cases where we want to contain the image in some container, rather
 * than have it cover that container. For example, when using images provided
 * by third parties on cards where the card has a landscape aspect ratio but
 * the returned image has a portrait aspect ratio. In such cases, we choose
 * whether or not to use the `fit` instead of the `resize` param when
 * calling our image service, based on various additonal conditions.
 *
 * Any subsequent resize logic can continue as normal. The result is that we
 * get to maintain our srcset behaviour, just with additional whitespace
 * rendered around the image. This whitespace can be filled with whatever
 * background colour makes sense to the consumer.
 *
 * It is also possible to add the borders only if they would be applied above
 * and below the image, or to the left and right of the image by using the
 * contain horizontal and vertical modes, respectively.
 */
const getResizeStrategy = (ratio, width, height, contain) => {
  const actualRatio = height / width;
  if (!Number.isFinite(actualRatio)) {
    return contain ? 'fit' : 'resize';
  }
  if (!contain || !ratio) {
    return 'resize';
  }
  if (height > width && contain === 'all' || actualRatio < ratio && contain === 'horizontal') {
    return 'fit';
  }
  if (height <= width && contain === 'all' || actualRatio >= ratio && contain === 'vertical') {
    return 'fit';
  }
  return 'resize';
};

/**
 * Get the CSS pixel width of the image.
 */
const getCssPixelWidth = (width, maxWidth) => {
  if (Number.isFinite(width) && Number.isFinite(maxWidth) && width > maxWidth) {
    return maxWidth;
  }
  return width;
};

/**
 * Calculate an explicit width for the container, if possible.
 *
 * This is so that we can set both a max height and a max width, have the image
 * scale properly, and still reserve whitespace to prevent content reflow.
 */
const getContainerWidth = (width, height, ratio, maxHeight, maxWidth) => {
  const cssPixelWidth = getCssPixelWidth(width, maxWidth);

  // If the aspect ratio times the CSS pixel width would make the image greater
  // than the max height then recalculate the container width based on an
  // inverted variant of the aspect ratio. Assuming we have all the relevant
  // props to do this.
  if (Number.isFinite(ratio) && Number.isFinite(width) && Number.isFinite(height) && Number.isFinite(cssPixelWidth) && Number.isFinite(maxHeight) && ratio * cssPixelWidth > maxHeight) {
    return maxHeight * (width / height);
  }
  return cssPixelWidth || maxWidth;
};

/**
 * Get the CSS pixel height of the image.
 */
const getCssPixelHeight = (width, height, ratio, maxHeight, maxWidth) => {
  const cssPixelWidth = getCssPixelWidth(width, maxWidth);
  const containerWidth = getContainerWidth(width, height, ratio, maxHeight, maxWidth);

  // If possible, calculate from the CSS pixel width times the aspect ratio.
  if (cssPixelWidth && ratio) {
    return ratio * cssPixelWidth;
  }

  // Fallback for when no container width could be calculated and there is no
  // explicit height we can otherwise use to resize the image.
  if (!containerWidth && !height && maxHeight) {
    return maxHeight;
  }
  return height;
};

/**
 * Image component.
 */
const Image = ({
  className,
  dataIndex,
  gtmLabel,
  url,
  width,
  height,
  alt,
  title,
  caption,
  sourceName,
  sourceUrl,
  fluid,
  sizes,
  quality,
  crop,
  lbWidth,
  lbHeight,
  srcSetBreakpoints,
  external,
  aspectRatio,
  maxWidth,
  maxHeight,
  link,
  scaleUp,
  onError,
  onLoad,
  loading,
  loadingIcon,
  noPlaceholder,
  contain,
  fillContainerWidth
}) => {
  const ratio = getAspectRatio(aspectRatio, width, height);
  const resizeStrategy = getResizeStrategy(ratio, width, height, contain);
  process.env.NODE_ENV !== "production" ? warning(!maxHeight || fluid, 'If using the maxHeight property then you should also set fluid to true') : void 0;
  const cssPixelWidth = getCssPixelWidth(width, maxWidth);
  const containerWidth = getContainerWidth(width, height, ratio, maxHeight, maxWidth);
  const cssPixelHeight = getCssPixelHeight(width, height, ratio, maxHeight, maxWidth);
  process.env.NODE_ENV !== "production" ? warning(external || !url || url.indexOf('?') === -1, `URLs passed to <Image> should not have query parameters: ${url}`) : void 0;
  const commonImgArgs = {
    url: !external ? url === null || url === void 0 ? void 0 : url.split('?', 1)[0] : url,
    width,
    height,
    quality,
    crop,
    lbWidth,
    lbHeight,
    external,
    resizeStrategy,
    ratio
  };
  const Container = link ? Link : 'div';
  const Component = caption || sourceName ? 'figure' : 'div';

  // calculate max width.
  // maxWidth is applied to the outer <Component> unless fillContainerWidth, in which case
  // it's pushed down to the <Container> to allow centered images with full-width captions.
  const containerMaxWidth = scaleUp || !Number.isFinite(containerWidth) ? null : `${containerWidth}px`;

  // Sometimes we don't know the image height and width and so can't rely
  // on the browser reserving space for the image correctly. For images that
  // pass through our imaging service this should all be fine, but for images
  // provided via third-parties we may only have a URL. In these cases we need
  // to fall back to the old vertical padding trick to reserve whitespace while
  // the image loads prevent content reflow.
  //
  // TODO: remove this when CSS aspect-ratio's widely supported
  const useReservedSpaceFallback = !(width && height) && ratio;
  const isAmp = useAmp();
  const [showPlaceholder, setShowPlaceholder] = useState(false);

  /**
   * Show a placeholder if the image fails to load and call an optional callback.
   */
  const handleError = evt => {
    setShowPlaceholder(true);
    if (onError) {
      onError(evt);
    }
  };
  const [hideLoadingIcon, setHideLoadingIcon] = useState(false);

  /**
   * Hide the loading icon if enabled and call an optional callback.
   */
  const handleLoad = img => {
    if (loadingIcon) setHideLoadingIcon(true);
    if (onLoad) onLoad(img);
  };

  // If the image URL changes, hide the placeholder and attempt
  // to load the new image.
  useEffect(() => {
    setShowPlaceholder(false);
  }, [url]);
  return /*#__PURE__*/React.createElement(Component, {
    className: cn("image chromatic-ignore", className, fluid && 'image--fluid', scaleUp && 'image--scaled-up', useReservedSpaceFallback && 'image--reserved-space-fallback'),
    style: {
      maxWidth: !fillContainerWidth ? containerMaxWidth : null
    }
  }, !showPlaceholder && url ? /*#__PURE__*/React.createElement(Container, _extends({
    className: ratio ? "image__container" : "",
    style: {
      paddingBottom: useReservedSpaceFallback ? `${(ratio * 100).toFixed(2)}%` : null,
      maxWidth: fillContainerWidth ? containerMaxWidth : null
    }
  }, link), isAmp ?
  /*#__PURE__*/
  // Note that sizes is not added to AMP images because of inconsistencies
  // with the native img https://github.com/ampproject/amphtml/issues/17053
  React.createElement("amp-img", {
    src: getImageUrl({
      ...commonImgArgs,
      width: cssPixelWidth,
      height: cssPixelHeight,
      webp: true
    }),
    alt: alt,
    layout: "responsive",
    srcSet: generateSrcSet({
      ...commonImgArgs,
      webp: true,
      srcSetBreakpoints
    }),
    width: cssPixelWidth,
    height: cssPixelHeight
  }, /*#__PURE__*/React.createElement("amp-img", {
    fallback: "",
    src: getImageUrl({
      ...commonImgArgs,
      width: cssPixelWidth,
      height: cssPixelHeight
    }),
    alt: alt,
    layout: "responsive",
    srcSet: generateSrcSet({
      ...commonImgArgs,
      srcSetBreakpoints
    }),
    width: cssPixelWidth,
    height: cssPixelHeight
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, loadingIcon ? /*#__PURE__*/React.createElement(LoadingIcon, {
    className: "image__loading-icon p-absolute",
    fadeOut: hideLoadingIcon
  }) : null, /*#__PURE__*/React.createElement("picture", {
    className: "image__picture",
    width: cssPixelWidth,
    height: cssPixelHeight
  }, !external && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("source", {
    type: "image/webp",
    sizes: sizes,
    srcSet: generateSrcSet({
      ...commonImgArgs,
      webp: true,
      srcSetBreakpoints
    })
  }), /*#__PURE__*/React.createElement("source", {
    sizes: sizes,
    srcSet: generateSrcSet({
      ...commonImgArgs,
      srcSetBreakpoints
    })
  })), /*#__PURE__*/React.createElement("img", {
    className: 'image__img' + (loadingIcon && !hideLoadingIcon ? " image__img--fade-out" : ""),
    src: getImageUrl({
      ...commonImgArgs,
      width: cssPixelWidth,
      height: cssPixelHeight
    }),
    alt: alt,
    "data-index": dataIndex,
    "data-item-name": alt,
    title: title,
    style: {
      aspectRatio: Number.isFinite(ratio) ? `1 / ${ratio}` : null,
      objectFit: resizeStrategy === 'fit' ? 'contain' : 'cover'
    },
    onError: handleError,
    onLoad: handleLoad,
    loading: width && height ? loading : 'auto',
    width: cssPixelWidth,
    height: cssPixelHeight
  })))) : ratio && !noPlaceholder &&
  /*#__PURE__*/
  // Fallback placeholder, if we can determine an aspect ratio
  React.createElement(Placeholder, {
    aspectRatio: ratio
  }), (caption || sourceName) && /*#__PURE__*/React.createElement(EditorContent, {
    gtmLabel: gtmLabel ? `${gtmLabel}-img-caption` : null,
    wrapper: "figcaption"
  }, caption ? parseHtml(sourceName ? `${caption}&nbsp;` : caption) : '', sourceName ? (() => {
    const SrcComponent = sourceUrl ? Link : 'span';
    const linkProps = sourceUrl ? {
      // props which only have a use when element is clickable
      href: sourceUrl,
      gtmLabel: gtmLabel ? `${gtmLabel}-img-src-link` : null // must be included here to prevent warning due to camel casing
    } : null;
    return /*#__PURE__*/React.createElement(SrcComponent, _extends({
      className: "fs-italic"
    }, linkProps), sourceName);
  })() : null));
};
Image.defaultProps = {
  className: '',
  dataIndex: null,
  gtmLabel: null,
  url: null,
  width: null,
  height: null,
  alt: '',
  title: null,
  caption: null,
  sourceName: null,
  sourceUrl: null,
  quality: 90,
  fluid: false,
  sizes: [],
  srcSetBreakpoints: [],
  crop: null,
  lbWidth: null,
  lbHeight: null,
  external: false,
  aspectRatio: null,
  maxWidth: null,
  maxHeight: null,
  link: null,
  scaleUp: false,
  onError: null,
  onLoad: null,
  loading: 'lazy',
  loadingIcon: false,
  noPlaceholder: false,
  contain: null,
  fillContainerWidth: false
};
Image.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional classes to be added to the root of the component. */
  className: string,
  /** The index of the image. */
  dataIndex: number,
  /** A label to be used for GTM tracking. */
  gtmLabel: string,
  /** The base URL of the image. */
  url: string,
  /** The original width of the image. */
  width: number,
  /** The original height of the image. */
  height: number,
  /** The aspect ratio used to scale the image. */
  aspectRatio: oneOfType([number, string]),
  /** The width when adding a letterbox effect to images. */
  lbWidth: number,
  /** The height when adding a letterbox effect to images. */
  lbHeight: number,
  /** Alternate text for the image. */
  alt: string,
  /** The title of the image. */
  title: string,
  /* The image caption */
  caption: string,
  /* The image source text */
  sourceName: string,
  /* The image source URL */
  sourceUrl: string,
  /** The quality of the image. */
  quality: oneOf(Array.from(Array(101).keys())),
  /** Make the image fit to the width of its container. */
  fluid: bool,
  /** The responsive sizes for the image. */
  sizes: array,
  /** A crop of the original image, in pixels, in the format [x,y,w,h]. */
  crop: arrayOf(number),
  /** The breakpoints at which to generate a srcset item. */
  srcSetBreakpoints: arrayOf(number),
  /** Render the raw image source only, without passing any parameters through
   to the image service. */
  external: bool,
  /** The maximum width of the image, in CSS pixels. */
  maxWidth: number,
  /** The maximum height of the image, in CSS pixels. */
  maxHeight: number,
  /** Properties for a link to wrap around the image container. */
  link: shape(Link.propTypes),
  /** Allow the image to scale up beyond the maximum size available. This may
    result in grainy images but is necessary for some layouts. */
  scaleUp: bool,
  /** Callback for when the image fails to load. */
  onError: func,
  /** Callback for when the image loads. */
  onLoad: func,
  /* Lazy loading strategy */
  loading: oneOf(['lazy', 'eager', 'auto']),
  /** Show a loading icon until the image has loaded. */
  loadingIcon: bool,
  /** Do not show the placeholder if the image fails to load. */
  noPlaceholder: bool,
  /** Contain, rather than cover the image container. */
  contain: oneOf(['all', 'vertical', 'horizontal']),
  /** Expand the image container to the full width of the parent. */
  fillContainerWidth: bool
} : {};
export default Image;